import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { flattenObject, getS3TimeKeys } from '@iotv/datamodel-core';
import { ErrDataReducer, getZombieInstanceFromPrimaryKey, isDatastoreKey, isNonNaNNumber, logLevels, } from '@iotv/iotv-v3-types';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Chip, IconButton, LinearProgress, MenuItem, Select, Stack, TextField } from '@mui/material';
import React, { useEffect, useReducer } from 'react';
import { getObjectFromS3, s3ListWithCors } from '../../data/aws/s3/api';
import { exportToJson } from '../../data/jsonExport';
import { AppLocalStorage } from '../../data/LocalStorage/AppLocalStorage';
import { useKeySelector } from '../../hooks/useKeySelector';
import { useModularTimePeriodSelector } from '../../hooks/useModularTimePeriodSelector';
import { ActionIcons } from '../../Iconography';
import { ventureImports } from '../../venture';
import { ItemLink } from '../common/ItemLinkList';
import { NavSections } from '../navigation/NavSections';
import { LogEntriesGrid } from './LogEntriesGrid';
import { defaultLogState, getS3Key, logReadableNames, LogViewerContext, prefixes, s3TimeLevel, } from './LogViewerConstants';
import { LogItems } from './SingleLogItem';
const { REACT_APP_PROJECT_CODE: projectCode, REACT_APP_DEPLOYMENT_STAGE: stage, REACT_APP_DEBUG } = process.env;
const debug = REACT_APP_DEBUG && false;
const bucketIdentifier = `${projectCode}-${stage}-logs-main`;
debug && console.log('LogViewer has additionalS3LogBuckets', ventureImports);
export const LogViewer = (props) => {
    const [state, dispatch] = useReducer((prev, next) => {
        return { ...prev, ...next };
    }, defaultLogState);
    const { containedItems, contentRecords, fetchingRecords, defaultInspectDepth, groupArraysAfterLength, fetchingEntry, selectedEntry, selectedService, selectionModel, } = state;
    const [first] = selectionModel.ids;
    const selectedEntryKey = first;
    const getLogObject = async ({ Key }) => {
        dispatch({ selectedEntry: undefined, containedItems: [], fetchingEntry: true });
        const { err, data } = await getObjectFromS3({ bucketIdentifier, Key });
        if (err || !data) {
            console.error('LogViewer', 'getLogObject', err ?? 'No data');
            dispatch({ fetchingEntry: false });
        }
        if (data) {
            const containedItems = Object.entries(flattenObject(data))
                .map(([k, v]) => v)
                .filter(isDatastoreKey)
                .filter((sk) => sk.split(':').length === 2)
                .reduce((a, c) => {
                if (!a.includes(c))
                    a.push(c);
                return a;
            }, []);
            dispatch({ selectedEntry: data, containedItems, fetchingEntry: false });
        }
    };
    const listLogs = async ({ fromDateValue, toDateValue }) => {
        dispatch({ fetchingRecords: true });
        const s3RangeKeys = getS3TimeKeys(s3TimeLevel, fromDateValue, toDateValue);
        debug && console.log('listLogs rangeKeys', s3RangeKeys);
        const promises = await Promise.all(s3RangeKeys.map(async (s3RangeKey) => {
            const s3KeyPrefix = getS3Key(selectedService, s3RangeKey);
            const listRes = s3KeyPrefix ? await s3ListWithCors(s3KeyPrefix, bucketIdentifier) : { err: null, data: null };
            return listRes;
        }));
        const { err, data } = ErrDataReducer(promises);
        if (err || !data) {
            console.error('LogViewer', err?.message ?? 'No response', promises);
            dispatch({ fetchingRecords: false });
        }
        if (data) {
            const contents = data.reduce((acc, s3ListRes) => {
                if (s3ListRes.Contents) {
                    acc.push(...s3ListRes.Contents);
                }
                return acc;
            }, []);
            debug && console.log('listLogs contents', contents);
            dispatch({ contentRecords: contents, fetchingRecords: false });
        }
    };
    const setSelectedServiceWrapper = (selectedService) => {
        AppLocalStorage.set(NavSections.SYSTEM_LOGS, 'selectedService', selectedService);
        dispatch({ selectedService });
    };
    const refreshSelectedEntries = () => {
        debug && console.log('refreshing entries');
        listLogs({ fromDateValue, toDateValue });
    };
    const handleLevelChange = (e) => {
        const v = parseInt(e.target.value);
        if (isNonNaNNumber(v) && v > 0) {
            dispatch({ groupArraysAfterLength: v });
        }
    };
    const handleDepthChange = (e) => {
        const v = parseInt(e.target.value);
        if (isNonNaNNumber(v) && v > 0) {
            dispatch({ defaultInspectDepth: v });
        }
    };
    const { components: timeSelectorComponents, fromDateValue, toDateValue, } = useModularTimePeriodSelector({ rangeUnit: 'minutes', rangeLength: 10, allowedRangeUnits: ['minutes'], requireConfirmation: true });
    useEffect(() => {
        if (selectedService && fromDateValue && toDateValue) {
            listLogs({ fromDateValue, toDateValue });
            dispatch({ selectedEntry: undefined });
        }
    }, [selectedService, fromDateValue, toDateValue]);
    useEffect(() => {
        if (selectedEntryKey)
            getLogObject({ Key: selectedEntryKey });
        else
            dispatch({ selectedEntry: undefined, containedItems: [] });
    }, [selectedEntryKey]);
    const { requiredKeys, component: KeySelector } = useKeySelector({
        initialKeys: logLevels,
        initialRequiredKeys: ['WARN', 'ERROR', 'FATAL'],
        sort: false,
        wrap: false,
    });
    const processSelectedEntry = (selectedEntry) => {
        const entry = selectedEntry;
        debug && console.log('processSelectedEntry has', entry);
        const extracted = entry.filter((message) => requiredKeys.includes(message.level));
        debug && console.log('processSelectedEntry filtered to', extracted);
        return extracted;
    };
    const displayObject = selectedEntry ? processSelectedEntry(selectedEntry) : undefined;
    return (_jsx(LogViewerContext.Provider, { value: { state, dispatch }, children: _jsxs(Stack, { height: "100cqh", gap: 1, children: [_jsxs(Stack, { gap: 1, children: [_jsxs(Stack, { direction: "row", justifyContent: "space-between", children: [_jsxs(Stack, { direction: "row", gap: 1, children: [_jsxs(Select, { value: selectedService,
                                            size: 'small',
                                            style: { width: 250 },
                                            onChange: (e) => setSelectedServiceWrapper(e.target.value), children: [Object.entries(prefixes).map(([k, prefix]) => (_jsx(MenuItem, { value: prefix, children: logReadableNames[k] }, prefix))), ventureImports.additionalS3LogBuckets &&
                                                    Object.entries(ventureImports.additionalS3LogBuckets).map(([k, prefix]) => (_jsx(MenuItem, { value: prefix, children: k }, prefix)))] }), _jsx(IconButton, { onClick: refreshSelectedEntries, children: _jsx(RefreshIcon, {}) })] }), _jsxs(Stack, { direction: "row", gap: 1, children: [_jsx(TextField, { size: 'small', label: 'Group Level', type: 'number', value: groupArraysAfterLength, onChange: handleLevelChange }), _jsx(TextField, { size: 'small', label: 'Depth Level', type: 'number', value: defaultInspectDepth, onChange: handleDepthChange })] })] }), _jsx(Box, { children: _jsxs(Stack, { direction: 'row', gap: 1, children: [timeSelectorComponents.periodOrRecentRadio, timeSelectorComponents.shiftEarlierBtn, timeSelectorComponents.fromPicker, timeSelectorComponents.periodField, timeSelectorComponents.toPicker, timeSelectorComponents.shiftLaterBtn, timeSelectorComponents.resetToNowBtn, timeSelectorComponents.confirmButton] }) }), _jsxs(Stack, { direction: "row", justifyContent: "space-between", children: [_jsx(Stack, { direction: "row", minHeight: "2em", flexWrap: "wrap", gap: 0.5, children: containedItems.map((sk) => (_jsx(Chip, { color: "info", label: _jsx(ItemLink, { item: getZombieInstanceFromPrimaryKey(sk, { name: sk }) }) }, sk))) }), _jsx(Stack, { direction: "row", minHeight: "2em", flexWrap: "nowrap", justifyContent: "flex-end", alignItems: "center", gap: 0.5, children: KeySelector })] })] }), _jsxs(Stack, { direction: "row", height: "calc(100% - 8em)", gap: 1, children: [_jsx(Stack, { width: "20%", children: _jsx(LogEntriesGrid, { items: contentRecords,
                                loading: fetchingRecords,
                                selectionModel,
                                setSelectionModel: (selectionModel) => dispatch({ selectionModel }) }) }), _jsxs(Stack, { width: "80%", gap: 1, sx: { overflowY: 'auto' }, children: [fetchingEntry && _jsx(LinearProgress, {}), selectedEntryKey && displayObject && (_jsx(Stack, { direction: "row", justifyContent: "flex-end", gap: 1, children: _jsx(IconButton, { onClick: () => exportToJson(`${selectedEntryKey}.json`, displayObject), children: _jsx(ActionIcons.Export, { color: "success" }) }) })), selectedEntryKey && displayObject && _jsx(LogItems, { logs: displayObject }, selectedEntryKey + requiredKeys.join())] })] })] }) }));
};
